// maplibre-gl.css is deliberately NOT imported here.
//
// Importing it from the entry put it in the entry CSS chunk, which means a
// render-blocking <link> in <head> — 10 kB the first paint cannot use, because
// the map engine itself is dynamically imported and has not arrived yet.
// Lighthouse flagged it, and on throttled mobile it is real time on the clock.
// It now loads with the map chunk, from map.ts.
//
// That flips the cascade: our stylesheet loads first and maplibre's second, so
// the handful of rules that override it at *equal* specificity would lose. They
// have been made one class more specific instead of relying on source order —
// see the popup section in style.css. Order was always the fragile way to win
// this, since a bundler is free to reorder CSS chunks.
import './style.css';

import {
  busStats, fetchAircraft, fetchBuses, fetchRiverHistory, fetchRoute, fetchRoutes,
  fetchStops, fetchTrains,
  fetchTideDay, fetchVessels, trainStats, type RiverWindow,
  type RouteName,
} from './api.ts';
import { escapeHtml } from './escape.ts';
import { renderLevelChart } from './sparkline.ts';
import { renderTideChart } from './tide-chart.ts';
import { initAnalytics, track } from './analytics.ts';
import { wireReport } from './report.ts';
import { shareMessage, shareView } from './share.ts';
import {
  fetchWeather, renderAirport, renderBusStation, renderFerries, renderFerryTerminal,
  renderRail, renderRailDisruption, renderStopBoard, renderWeather,
} from './panels.ts';
import {
  featureIdCandidates, isFixed, parseRoute, parseTarget, withRoute, withTarget, type Target,
} from './deep-link.ts';
import { createPanels } from './floating.ts';
import { PALETTE } from './icons.ts';
import { Interpolator } from './interpolate.ts';
import { icon } from './layer-icons.ts';
import {
  CAMERA_STALE_MS,
  FUBAR_RED, SEVERITY_COLOUR, applyLayerHealth, boxHead, fetchGeometry, fetchLayers,
  fetchLiveFeatures,
  fetchStatus,
  renderEventsBox, renderFeatureList, renderIncident, renderRoadsBox, renderStatusCard,
  renderTransportBox, renderEnvironmentBox, renderFuelTop,
  type LiveFeature,
} from './live.ts';
import { fuelView, parseFuelGrade, type FuelGrade } from './fuel-grade.ts';
import { watchBrokenImages } from './hide-broken-images.ts';
import { isQuiet, zoneColour } from './map-style.ts';
import { registerServiceWorker } from './register-sw.ts';
import type { MapAdapter, StopPoint } from './map.ts';
import { showMapFailure } from './no-webgl.ts';
import { dedupeStops, rank, type SearchItem } from './search.ts';
import { wireSearch } from './search-ui.ts';
import type { Kind, Vehicle } from './types.ts';
import {
  applySelection, openingSelection, selectionFor, selectionToSearch, writeStored,
} from './view-state.ts';

/**
 * What colour a feature's own shape is painted, by the sprite it carries.
 *
 * The journey-time key, and it is deliberately the stopwatch palette rather
 * than the severity one: the marker and the road it sits on are the same
 * reading, and a reader who has learnt that the green stopwatch means moving
 * should not have to learn a second green. Anything not listed falls back to
 * its severity colour, which is what every other layer would get if it ever
 * grew a shape.
 */
const LINE_COLOUR: Record<string, string> = {
  journey: PALETTE.journey,
  'journey-slow': PALETTE.journeySlow,
  'journey-stopped': PALETTE.journeyStopped,
};

/**
 * Poll cadences, matched to how fast each thing actually moves and to the
 * backend's own upstream intervals. Polling faster than the backend refreshes
 * only wastes battery.
 */
const POLL_MS: Record<Kind, number> = {
  aircraft: 10_000,
  vessels: 20_000,
  buses: 20_000,
  // Darwin is polled once a minute server-side, but a train's computed
  // position changes continuously between its anchors, so asking more often
  // than the source updates still moves the dot.
  trains: 30_000,
};

/**
 * A one-off delay added after each layer's first fetch, to pull the polls apart.
 *
 * All four loops used to start in the same millisecond, and every interval is a
 * multiple of ten seconds, so they re-collided every minute for as long as the
 * tab stayed open: four responses parsed and four map sources rewritten in the
 * same frame. On a desktop that is invisible; on a phone it is a periodic
 * stutter, which is the thing most likely to make someone close the tab.
 *
 * Offsets are 2.5s apart and every interval is a multiple of 10s, so no two
 * loops ever land on the same tick again. The first fetch is not delayed —
 * someone waiting for the map to fill should not wait 7.5s for trains.
 */
const PHASE_MS: Record<Kind, number> = {
  aircraft: 0,
  vessels: 2_500,
  buses: 5_000,
  trains: 7_500,
};

/** Every vehicle layer this frontend can draw, before the place has spoken. */
const ALL_LAYERS: { kind: Kind; label: string; colour: string }[] = [
  { kind: 'aircraft', label: 'Aircraft', colour: PALETTE.plane },
  { kind: 'vessels', label: 'Ships', colour: PALETTE.ship },
  { kind: 'buses', label: 'Coaches', colour: PALETTE.bus },
  // Labelled as an estimate in the legend itself, not only in the popup: the
  // count is the first thing read, and it should not imply observed fixes.
  { kind: 'trains', label: 'Trains (est.)', colour: PALETTE.train },
];

/**
 * The ones this place actually has — narrowed once, from /meta, before the
 * polls start.
 *
 * The list used to be a constant, so every city rendered every row. On a city
 * with no water that meant a ticked "Ships" box reporting 0 for ever, and a
 * poll every 20 seconds for a list the backend could never fill. The map said
 * the layer was on, the status page did not list the feed at all, and nothing
 * anywhere said why — which is a worse answer than no row.
 *
 * One binding, filtered in place, because it is read by the poll loops, the
 * frame loop, the outage check and the count render: a second list would be
 * four chances for the two to disagree.
 */
let LAYERS = ALL_LAYERS;

/**
 * Which of them the backend says this place has.
 *
 * Only an explicit `enabled: false` removes a row. /meta lists the layers it
 * knows about and says nothing about trains, and a missing key must not be
 * read as a refusal — a fallback meta from a backend that is down carries no
 * layers at all, and blanking the legend at exactly the moment the API is
 * struggling is the opposite of useful.
 */
function layersFor(meta: { layers?: Record<string, { enabled?: boolean }> }) {
  return ALL_LAYERS.filter(({ kind }) => meta.layers?.[kind]?.enabled !== false);
}

const FETCHERS: Record<Kind, () => Promise<Vehicle[]>> = {
  aircraft: fetchAircraft,
  vessels: fetchVessels,
  buses: fetchBuses,
  trains: fetchTrains,
};

const interpolators: Record<Kind, Interpolator> = {
  aircraft: new Interpolator(),
  vessels: new Interpolator(),
  buses: new Interpolator(),
  trains: new Interpolator(),
};

const counts: Record<Kind, number> = { aircraft: 0, vessels: 0, buses: 0, trains: 0 };
const enabled: Record<Kind, boolean> = { aircraft: true, vessels: true, buses: true, trains: true };
const failures: Record<Kind, number> = { aircraft: 0, vessels: 0, buses: 0, trains: 0 };
let lastSuccessAt = 0;
/** null until the map reports either a drawn basemap or having given up. */
let basemapOk: boolean | null = null;

/**
 * When the API stopped answering, or 0 while it is answering.
 *
 * A single upstream going quiet does not set this: the backend replies 200 with
 * an empty list when Darwin or ADS-B is down, so the fetch succeeds. Only the
 * API itself being unreachable — a restart, a dead box, no network — makes
 * every poll throw at once, which is what this watches for.
 */
let apiDownSince = 0;

/**
 * How long the API must be away before saying so.
 *
 * A deploy or the Monday timetable swap restarts the backend for a few seconds,
 * and the proxy holds requests open across most of that. Announcing an outage
 * for a blip the visitor would never otherwise notice makes the site look less
 * reliable than it is, not more honest.
 */
const OUTAGE_NOTICE_AFTER_MS = 15_000;

/** Tram stops in this place, from /meta — the gate for the tram toggle. */
let tramStopCount = 0;
/** Subway stations, same source and same gating rule. */
let subwayStopCount = 0;
/** The place's name, for the camera control room's heading — never hardcoded. */
let placeName = '';
/** Road extents from the place pack, for the control room's group headings. */
let roadExtents: Record<string, string> = {};

/**
 * Switch on whatever layer a feature belongs to, so it can be focused.
 *
 * A seam between the two halves of this file rather than a nicety. Anything that
 * *opens* a feature — a status line, a row in a popped-out list — is making the
 * same promise: click this and you will see the thing. But the map only knows
 * about features whose layer is switched on, so on a layer that is off the
 * promise breaks silently: focusIncident looks up an id that was never given to
 * it and returns.
 *
 * The status-line path had this right and did it inline. The list rows are in
 * wirePins, which owns the panel and knows nothing about layer state, while the
 * set of hidden providers lives in wireLive, which owns the feed. Assigned once
 * by wireLive, and a no-op until then — before the layer tree has loaded there
 * is nothing hidden to reveal.
 */
let revealFeature: (featureId: string) => void = () => {};

/**
 * The live features, as things a reader can search for (#56).
 *
 * A second hook rather than an export, for the reason revealFeature above is
 * one: the features live inside wireLive's closure because they are replaced
 * wholesale by a poll, and anything holding its own copy would be searching
 * whatever the map was drawing at boot. A no-op until that poll has landed —
 * which is honest, since before it there is nothing on the map to find.
 */
let searchableFeatures: () => SearchItem[] = () => [];

async function main(): Promise<void> {
  // First, so the pageview is counted even if a data fetch below throws.
  // No-op unless the Umami variables are set — see analytics.ts.
  initAnalytics();

  // Decorative images that fail — the Roads box credit, the FUBAR popup icon,
  // the control-room footer — hide instead of showing the broken-image
  // marker. Delegated rather than per-element because popups and the control
  // room inject their <img> tags long after this line runs (#74).
  watchBrokenImages();

  // Offline shell and the install prompt (#57). Registered on window load
  // inside, so it competes with nothing on the first paint, and a no-op in
  // dev and anywhere the registration is refused.
  registerServiceWorker();

  // Paint the answer to "what is happening in Aberdeen right now" before
  // anything heavy loads. MapLibre is roughly 800 KB of the bundle, and this
  // card needs none of it — so it is rendered from a single small request
  // while the map engine is still downloading in parallel.
  const statusEl = document.getElementById('status-card')!;
  wireStatusCollapse(statusEl);
  publishNowClearance(statusEl);
  const earlyStatus = fetchStatus()
    .then((s) => { renderStatusCard(statusEl, s.items); return s; })
    .catch(() => null);

  const [meta, { createMap }] = await Promise.all([
    // Aberdeen fallback so a backend that is down or slow still yields a
    // usable map rather than a blank page.
    // Bounded, because this await is the first paint: a backend mid-restart
    // held this open for a measured 21 seconds while Caddy queued it, and the
    // page sat dark the whole time. Five seconds of patience, then the
    // fallback map — the polls below fill everything in when the API wakes.
    fetch(`${import.meta.env.VITE_API_BASE || ''}/api/v1/meta`,
      { signal: AbortSignal.timeout(5_000) })
      .then((r) => r.json())
      .catch(() => ({ centre: { lat: 57.15, lon: -2.1 } })),
    // Dynamic import: Vite splits maplibre-gl into its own chunk, so it no
    // longer blocks first paint.
    import('./map.ts'),
  ]);
  await earlyStatus;

  // The header said Aberdeen for as long as there was only Aberdeen. The
  // fallback meta has no name, so a dead backend keeps the built-in title.
  if (meta.name) {
    document.title = `${meta.name} Live`;
    const h1 = document.querySelector('#titlebar h1');
    if (h1) h1.textContent = `${meta.name} Live`;
    // The strapline named Aberdeen too — the first thing a reader sees after
    // the title, and the last hardcoded place-name on the page.
    const sub = document.getElementById('subtitle');
    if (sub) sub.textContent = `what is happening in ${meta.name} right now`;
  }

  LAYERS = layersFor(meta as { layers?: Record<string, { enabled?: boolean }> });
  tramStopCount = Number(meta.tramStops ?? 0);
  subwayStopCount = Number(meta.subwayStops ?? 0);
  placeName = String(meta.name ?? 'Aberdeen');
  roadExtents = (meta.roadExtents ?? {}) as Record<string, string>;
  // A map that cannot start must say so. `new maplibregl.Map()` throws
  // synchronously when the browser will not give it a WebGL context, and main()
  // is async: the rejection goes to the console and the reader is left with the
  // black #map rectangle and no text on it, which is indistinguishable from a
  // map of somewhere unlit. See no-webgl.ts for the console output that cost an
  // afternoon. Nothing below this line works without a map, so this returns.
  let map: MapAdapter;
  try {
    map = createMap('map', meta.centre);
  } catch (err) {
    showMapFailure(document.body, document.title);
    // Kept, because the message deliberately does not print the engine's own
    // error and somebody debugging one report will want it.
    console.error('The map engine could not start', err);
    return;
  }
  map.onBasemapState((ok) => { basemapOk = ok; });

  // 3D, only for a city whose tilesets are published and verified —
  // __SERVING_3D__ is null for every other build, and with it the button,
  // the zoom watcher and the whole Cesium pathway simply do not exist.
  // See frontend/serving.ts for what "verified" means here.
  if (__SERVING_3D__) {
    const mapEl = document.getElementById('map');
    if (mapEl) {
      const { initGlobeMode } = await import('./globe-mode.ts');
      initGlobeMode(mapEl, map, __SERVING_3D__, (uid) => {
        if (uid) track('globe_pick', { uid });
      });
    }
  }

  /**
   * The share button (#53).
   *
   * `location.href` is read at click time rather than built here, because by
   * then it is already the whole answer: MapLibre keeps the viewport in the
   * hash and rememberView() keeps the layer selection in the query, so
   * whatever is in the address bar is exactly what is on screen.
   *
   * The button reports back on itself rather than through a toast — it is the
   * thing that was pressed, so it is where the eye already is, and it needs no
   * dismissing. Nothing is said when the platform sheet handled it: the reader
   * has just watched that animation and does not need telling twice.
   */
  function wireShare(): void {
    const button = document.getElementById('share-link');
    if (!(button instanceof HTMLButtonElement)) return;

    let restore: ReturnType<typeof setTimeout> | undefined;
    button.addEventListener('click', async () => {
      const outcome = await shareView(location.href, document.title, {
        share: navigator.share ? (data) => navigator.share(data) : undefined,
        copy: navigator.clipboard ? (text) => navigator.clipboard.writeText(text) : undefined,
      });
      track('share', { outcome, from: 'header' });

      const message = shareMessage(outcome);
      if (!message) return;

      const previous = button.title;
      button.title = message;
      button.classList.toggle('shared', outcome === 'copied');
      clearTimeout(restore);
      restore = setTimeout(() => {
        button.title = previous;
        button.classList.remove('shared');
      }, 2400);
    });
  }

  buildDock();
  const pins = pinsFromMeta(meta);
  // Before both panels, because both draw routes onto the same map and the
  // drawn route belongs to neither of them.
  const routes = wireRoutes(map);
  const openDetail = wirePins(map, pins, routes);
  const stopControls = wireStops(map, routes, openDetail);
  wireWeather();
  wireReport(map);
  wireShare();
  void wireLive(map, openDetail, String(meta.place ?? ''), stopControls);

  // A shared stop or station, opened as soon as there is a map to open it on
  // (#61). Not awaited: everything below this is the map running normally, and
  // a link that has to fetch 100 kB of stops must not hold it up.
  void openLinkedTarget(map, pins, stopControls, openDetail, routes);

  wireSearchBox(map, pins, stopControls, openDetail, routes, placeName);

  for (const { kind } of LAYERS) {
    void pollLoop(kind);
  }

  // Redraw on animation frames so interpolated motion is smooth, but skip the
  // work entirely when the tab is hidden — a background tab repainting a map
  // is pure battery drain, which matters more once this is a phone app.
  const frame = () => {
    if (!document.hidden) {
      const now = Date.now();
      const all: Vehicle[] = [];
      for (const { kind } of LAYERS) {
        if (enabled[kind]) all.push(...interpolators[kind].sample(now));
      }
      map.setVehicles(all);
      renderStatus();
    }
    requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);
}

async function pollLoop(kind: Kind): Promise<void> {
  let phase = PHASE_MS[kind];
  for (;;) {
    try {
      const vehicles = await FETCHERS[kind]();
      counts[kind] = vehicles.length;
      failures[kind] = 0;
      lastSuccessAt = Date.now();
      // One success is enough to clear the notice. The API is either there or
      // it is not, and making recovery wait for all four feeds would leave the
      // banner up over a single slow poll.
      apiDownSince = 0;
      // Ease over slightly longer than the poll gap: arriving a little late is
      // better than freezing at the destination waiting for the next update.
      interpolators[kind].update(vehicles, Date.now(), POLL_MS[kind] * 1.2);
      // The phase offset is spent once, on the first gap, and every gap after
      // it is the plain interval — so the loops stay pulled apart for good
      // rather than drifting back together.
      await sleep(POLL_MS[kind] + phase);
      phase = 0;
    } catch {
      failures[kind] += 1;
      // Every enabled feed failing means the API is unreachable, not that one
      // source is quiet. Timed from the first such moment rather than reset on
      // each new failure, or the clock would never advance.
      if (apiDownSince === 0 && LAYERS.every(({ kind: k }) => !enabled[k] || failures[k] > 0)) {
        apiDownSince = Date.now();
      }
      // Back off on repeated failure rather than hammering a struggling
      // backend, capped so recovery is still prompt.
      await sleep(Math.min(POLL_MS[kind] * 2 ** failures[kind], 120_000));
    }
  }
}

/**
 * Fills in the level chart when a river gauge's card opens.
 *
 * The card HTML is built before anyone clicks — it goes into the map source as
 * a string — so the chart cannot be part of it. This waits for the popup, then
 * fetches. Most gauges are never clicked, and a week of readings is 672 points
 * each, so fetching them all up front would be the wrong trade entirely.
 */
function wireRiverCharts(map: MapAdapter): void {
  map.onIncidentOpen((id, el) => {
    // Tides: one fetch, no tabs — a tide card shows one day and that is the
    // whole of it.
    const tideSlot = el.querySelector<HTMLElement>('.tide-chart-slot');
    const stationId = tideSlot?.dataset.station;
    if (tideSlot && stationId) {
      void fetchTideDay(stationId)
        .then((day) => { tideSlot.innerHTML = renderTideChart(day); })
        .catch(() => {
          // Name the failure. A blank space where a chart was announced reads
          // as our bug even when the tide service is the thing that is away.
          tideSlot.innerHTML = '<p class="rc-empty">Tide curve unavailable just now.</p>';
        });
    }

    if (!id.startsWith('river:')) return;
    const chart = el.querySelector<HTMLElement>('.river-chart');
    const tsId = chart?.dataset.ts;
    if (!chart || !tsId) return;

    const body = chart.querySelector<HTMLElement>('.rc-body');
    if (!body) return;

    // Tracks the newest request, so a slow 7d response cannot overwrite a 4h
    // one the user asked for afterwards.
    let latest = 0;

    const load = async (window: RiverWindow) => {
      const token = ++latest;
      body.innerHTML = '<p class="rc-empty">Loading levels…</p>';
      try {
        const history = await fetchRiverHistory(tsId, window);
        if (token !== latest) return;
        body.innerHTML = renderLevelChart(history);
      } catch {
        if (token !== latest) return;
        // Say what failed. A blank panel reads as our bug even when SEPA is
        // the thing that is away.
        body.innerHTML = '<p class="rc-empty">Levels unavailable just now.</p>';
      }
    };

    chart.querySelector('.rc-tabs')?.addEventListener('click', (e) => {
      const button = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-w]');
      const window = button?.dataset.w as RiverWindow | undefined;
      if (!window) return;
      for (const b of chart.querySelectorAll('.rc-tabs button')) b.classList.remove('on');
      button!.classList.add('on');
      void load(window);
    });

    void load('24h');
  });
}

/**
 * The polygon inside a feature's `area`, if it has one.
 *
 * `area` is typed `unknown` on the wire because it has only ever been "some
 * GeoJSON the provider knew about", and until the Low Emission Zones nothing
 * read it at all. This narrows it to the one thing the map can draw as a fill,
 * and returns undefined for anything else rather than guessing — a LineString
 * handed to a fill layer renders as nothing, silently.
 */
function polygonOf(area: unknown): { type: 'Polygon' | 'MultiPolygon'; coordinates: unknown }
| undefined {
  if (!area || typeof area !== 'object') return undefined;
  // Providers may send a bare geometry or a Feature wrapping one.
  const node = 'geometry' in area
    ? (area as { geometry: unknown }).geometry
    : area;
  if (!node || typeof node !== 'object') return undefined;
  const g = node as { type?: unknown; coordinates?: unknown };
  if (g.type !== 'Polygon' && g.type !== 'MultiPolygon') return undefined;
  if (!Array.isArray(g.coordinates) || g.coordinates.length === 0) return undefined;
  return { type: g.type, coordinates: g.coordinates };
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

// ── Floating boxes and the dock that restores them ──────────────────────────

/**
 * The dock glyph per box is the layer that box is named for — a plane for
 * Transport, a cloud for Weather — so the dock reads as the same vocabulary
 * as the rows inside each panel.
 *
 * Environment is the exception, and deliberately: it is a heading rather than
 * a layer, and every glyph it could borrow (river, waves, weather) already
 * belongs to a row inside it. It gets a leaf of its own instead.
 */
const BOXES = [
  { id: 'transport', label: 'Transport', icon: 'aircraft', colour: '76, 194, 255' },
  { id: 'environment', label: 'Environment', icon: 'environment', colour: '63, 181, 106' },
  // "Roads", not "Trunk roads", since the register's local works joined the
  // Traffic Scotland layers in this box — see GROUP_LABEL in live.ts.
  { id: 'roads', label: 'Roads', icon: 'roadworks', colour: '255, 159, 10' },
  // The Fringe star, in the layer's own pink — the same glyph the venue rows
  // carry, so the button and what it opens read as one thing. It stays the
  // Events glyph when the box grows past festivals: a star is what is on
  // tonight, which covers a match and a concert as readily as a show.
  { id: 'events', label: 'Events', icon: 'fringe', colour: '255, 92, 138' },
  { id: 'weather', label: 'Weather', icon: 'weather', colour: '139, 155, 173' },
] as const;

/**
 * Panel chrome — where each box sits, whether it is collapsed, whether it is
 * hidden — lives in floating.ts. Everything here only asks it questions.
 */
const panels = createPanels({
  ids: BOXES.map((b) => b.id),
  container: document.getElementById('boxes')!,
  // The map fills the window, so the window is the canvas a panel may roam.
  bounds: () => ({ width: window.innerWidth, height: window.innerHeight }),
  onVisibilityChange: () => { syncDock(); },
  absent: (id) => missingBoxes.has(id),
});

/**
 * Boxes this place has nothing to put in.
 *
 * Only Edinburgh sends event layers today, so on every other deployment the
 * Events box renders empty and `.box:empty` hides it. Its console button has to
 * go the same way or the console offers a glyph that does nothing — and a
 * control that appears to be broken is worse than a control that is absent.
 *
 * Starts empty and is filled once, after the layer fetch answers. That order
 * matters: every box is empty during the first paint, so a dock built from
 * emptiness alone would flash four buttons out and back in.
 */
const missingBoxes = new Set<string>();

function syncDock(): void {
  for (const b of BOXES) {
    const button = document.querySelector<HTMLElement>(`[data-dock="${b.id}"]`);
    if (!button) continue;
    button.classList.toggle('off', panels.isHidden(b.id));
    button.hidden = missingBoxes.has(b.id);
  }
}

/**
 * Record a layer being switched on or off, named so the answer is readable
 * without opening anything.
 *
 * Both halves go in the event *name* — `layer-off:tides`, not `layer-toggle`
 * with the rest in properties — because the question this exists to answer is
 * "is anybody turning this off?", and a dashboard's event list is a ranked
 * count of names. Properties are kept alongside for filtering, but a list of
 * fifteen identical `layer-toggle` rows answers nothing at a glance, which is
 * exactly what it did.
 *
 * Read the counts knowing which way each layer starts. A layer that is on by
 * default can only produce `layer-off` events from people who did not want it;
 * one that starts off produces `layer-on` from people who did. So the two are
 * not comparable as raw totals — the honest comparison is within a layer, or
 * between layers that share a default. Turning a default around on the back of
 * that mistake is the one way this data can actively mislead.
 *
 * Umami caps an event name at 50 characters. The prefix costs 10, and the
 * longest layer id today is `journey-times`, so the longest name we produce is
 * 23 — but a provider id over 40 characters would take the name past the cap,
 * and a rejected event is not something the map would ever notice. Keep new
 * provider ids short, which they are anyway for every other reason.
 */
function trackLayer(layer: string, on: boolean): void {
  track(`layer-${on ? 'on' : 'off'}:${layer}`, { layer, on });
}

/**
 * Fold the Now card down to its title bar, and remember it.
 *
 * Its own storage key and its own listener rather than joining the panel
 * machinery in floating.ts, because the card is not one of the panels: it has
 * no console button, no ×, no position, and it must never end up hidden. All it
 * shares with them is a chevron, so all it borrows is the chevron.
 *
 * The click is delegated from the card, which is the only reason this survives:
 * renderStatusCard replaces the whole card every sixty seconds, so a listener
 * bound to the button itself would be thrown away with the first refresh — and
 * the state has to be pushed back onto the new chevron each time for the same
 * reason.
 */
const STATUS_COLLAPSED_KEY = 'abz.nowCollapsed';

/**
 * How far the Now card reaches up from the bottom of the viewport, published
 * as `--now-clear` for the phone layout to lift the map controls by.
 *
 * On a phone the card is pinned above the dock, and MapLibre's zoom and
 * locate buttons live in the bottom-right corner of the map — so the card sat
 * on top of the zoom-out button (user report, Oct 2026: "the minus is masked
 * by the Now box"), and the scale bar in the other corner the same way. A
 * fixed offset in CSS would be right for exactly one height of a card that
 * folds, unfolds and changes length with the news, so this measures the card
 * itself and follows it. Desktop ignores the variable: the card is in the
 * top-left corner there and covers nothing at the bottom.
 */
function publishNowClearance(el: HTMLElement): void {
  const root = document.documentElement;
  const publish = () => {
    const rect = el.getBoundingClientRect();
    const clear = rect.height > 0 ? Math.max(0, window.innerHeight - rect.top) : 0;
    root.style.setProperty('--now-clear', `${Math.ceil(clear)}px`);
  };
  new ResizeObserver(publish).observe(el);
  window.addEventListener('resize', publish);
  publish();
}

function wireStatusCollapse(el: HTMLElement): void {
  let collapsed = false;
  try {
    collapsed = localStorage.getItem(STATUS_COLLAPSED_KEY) === '1';
  } catch {
    // Private browsing. It works, it just forgets.
  }

  const paint = () => {
    el.classList.toggle('collapsed', collapsed);
    const chevron = el.querySelector<HTMLButtonElement>('.st-collapse');
    if (!chevron) return;
    chevron.textContent = collapsed ? '\u25b8' : '\u25be';
    chevron.setAttribute('aria-expanded', String(!collapsed));
    chevron.setAttribute('aria-label', `${collapsed ? 'Expand' : 'Collapse'} Now`);
  };

  el.addEventListener('click', (e) => {
    if (!(e.target as HTMLElement | null)?.closest('[data-collapse-status]')) return;
    collapsed = !collapsed;
    try {
      localStorage.setItem(STATUS_COLLAPSED_KEY, collapsed ? '1' : '0');
    } catch {
      // As above.
    }
    paint();
    track(`now-${collapsed ? 'collapse' : 'expand'}`, { collapsed });
  });

  // Re-apply after every refresh: the card that was just written has a fresh
  // chevron in its default, expanded state.
  new MutationObserver(paint).observe(el, { childList: true });
  paint();
}

function buildDock(): void {
  const dock = document.getElementById('dock')!;
  dock.innerHTML = BOXES.map((b) => `
    <button class="dock-btn" data-dock="${b.id}" title="Show ${b.label}"
            aria-label="Show ${b.label}" style="--dock-rgb:${b.colour}">${icon(b.icon)}</button>`).join('');

  for (const button of dock.querySelectorAll<HTMLButtonElement>('.dock-btn')) {
    button.addEventListener('click', () => {
      // Toggle, so the dock both restores and re-hides — quicker than hunting
      // for the small × once you know what the icons mean.
      const id = button.dataset.dock!;
      panels.toggle(id);
      // Named the same way and for the same reason as trackLayer above.
      const shown = !panels.isHidden(id);
      track(`panel-${shown ? 'show' : 'hide'}:${id}`, { panel: id, shown });
    });
  }
  panels.apply();
}

interface PinMap {
  addPin(o: { lat: number; lon: number; label: string; onClick: () => void }): void;
  flyTo(lat: number, lon: number, zoom?: number): void;
  /** For panel rows that name an incident rather than a coordinate. */
  focusIncident(id: string): void;
}

/**
 * Put the open board in the address bar, or take it out again (#61).
 *
 * This is what makes a stop shareable at all. The share button reads
 * `location.href` at click time and deliberately knows nothing else; by then
 * this has already made that href the whole answer — the viewport in the hash,
 * the layers in `on`/`off`, and the stop right here.
 *
 * Every other parameter is passed through untouched, for the same reason
 * selectionToSearch() passes this one through: two writers of one query string
 * that each rebuild it from their own state take turns deleting each other's
 * half, and each looks correct in isolation. `marker` is the exception — it is
 * the older spelling of `go=feature:`, so leaving it would hand out a URL
 * naming two different things.
 *
 * replaceState, not pushState: opening a board is not a navigation, and a back
 * button that walks back through six stops before it will leave the page is
 * the failure rememberView() gives the same reason for.
 */
function markTarget(target: Target | null): void {
  history.replaceState(
    null, '', `${location.pathname}${withTarget(location.search, target)}${location.hash}`);
}

/**
 * Put the drawn route in the address bar, or take it out again (#61).
 *
 * Separate from markTarget() because the two are separate facts: the target is
 * the board that is open and the route is the line drawn from a row on it, and
 * a reader can have either without the other. Both preserve every parameter
 * they do not own, which is the only reason three writers of one query string
 * can coexist — see withTarget().
 */
function markRoute(route: string | null): void {
  history.replaceState(
    null, '', `${location.pathname}${withRoute(location.search, route)}${location.hash}`);
}

/**
 * The share glyph, same three circles as the one in the header.
 *
 * A constant, and the only reason it goes in through innerHTML: there is no
 * data in it. Everything variable on this button — the board's name — is set
 * through setAttribute and textContent instead.
 */
const SHARE_GLYPH = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">'
  + '<circle cx="17.5" cy="5.8" r="2.9" fill="none" stroke="currentColor" stroke-width="1.9" />'
  + '<circle cx="6.2" cy="12" r="2.9" fill="none" stroke="currentColor" stroke-width="1.9" />'
  + '<circle cx="17.5" cy="18.2" r="2.9" fill="none" stroke="currentColor" stroke-width="1.9" />'
  + '<path d="M8.8 10.7 14.9 7.2M8.8 13.3l6.1 3.5" stroke="currentColor"'
  + ' stroke-width="1.9" stroke-linecap="round" /></svg>';

/**
 * A share button in the open board's header, where the board is worth linking.
 *
 * Which boards those are is not a list kept here: the button appears exactly
 * when the URL already names what is open, so it cannot offer to share a link
 * that would not work. That falls out of markTarget() being the one writer —
 * a stop, a station, a harbour and a bus station all name themselves, and the
 * roadworks list that replaces them writes null and takes the button with it.
 * Fixed points get a link; things that come and go do not, because a link to
 * "2 power outages" is a link to nothing by the weekend.
 *
 * Re-run after every render, because the board re-renders itself once a minute
 * and takes its own header with it — the same reason the close button is
 * re-wired there rather than once.
 */
/**
 * Whether the open board is collapsed to its header.
 *
 * A reader taps a stop, taps a service, and the route draws underneath the
 * board that drew it. On a phone #detail is a bottom sheet 68vh tall, so
 * "underneath" means invisible, and the only way to see the line was to close
 * the board — which takes the line with it. The board and the route were an
 * either/or with no way to have both.
 *
 * Module state rather than a class on the panel, for the reason
 * wireStatusCollapse gives about the Now card: a board re-renders itself once
 * a minute and replaces its own header, so anything held in the DOM is lost on
 * the next tick and the panel silently springs open while somebody is reading
 * the map under it. The class is re-applied after every render instead.
 *
 * Not remembered across boards, and not in localStorage. Opening a stop is
 * asking for its departures; opening one to a bar with the name on it and
 * nothing else would be answering a question nobody asked.
 */
let boardCollapsed = false;

/** Put the state back on the panel and on whatever chevron is there now. */
function paintBoardCollapse(panel: HTMLElement): void {
  panel.classList.toggle('collapsed', boardCollapsed);
  const chevron = panel.querySelector<HTMLButtonElement>('.ap-collapse');
  if (!chevron) return;
  chevron.textContent = boardCollapsed ? '\u25b8' : '\u25be';
  chevron.setAttribute('aria-expanded', String(!boardCollapsed));
  chevron.setAttribute('aria-label',
    boardCollapsed ? 'Expand this board' : 'Collapse this board to see the map');
}

/**
 * The chevron that shrinks a board to its title bar.
 *
 * Injected rather than written into the markup because three different
 * renderers produce a board — a stop, a station, a pin panel — and every one
 * of them already grows this header the same way (see wireBoardShare). Called
 * after every render, like the close button and the share button, and for the
 * same reason: the header that was just written is a new element with no
 * handlers and no state on it.
 */
function wireBoardCollapse(panel: HTMLElement): void {
  const head = panel.querySelector('.ap-head');
  if (!head) return;
  if (!head.querySelector('.ap-collapse')) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'ap-collapse';
    button.addEventListener('click', () => {
      boardCollapsed = !boardCollapsed;
      paintBoardCollapse(panel);
      track(`board-${boardCollapsed ? 'collapse' : 'expand'}`);
    });
    head.insertBefore(button, head.querySelector('.ap-share') ?? head.querySelector('#ap-close'));
  }
  paintBoardCollapse(panel);
}

function wireBoardShare(panel: HTMLElement): void {
  const head = panel.querySelector('.ap-head');
  if (!head || head.querySelector('.ap-share')) return;
  if (!isFixed(parseTarget(location.search))) return;

  const name = head.querySelector('h2')?.textContent?.trim() ?? '';
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'ap-share';
  button.innerHTML = SHARE_GLYPH;
  const resting = name ? `Copy a link to ${name}` : 'Copy a link to this';
  button.title = resting;
  button.setAttribute('aria-label', name ? `Share ${name}` : 'Share this');
  head.insertBefore(button, head.querySelector('#ap-close'));

  let restore: ReturnType<typeof setTimeout> | undefined;
  button.addEventListener('click', async () => {
    // location.href, not a link rebuilt from the target: by the time this can
    // be clicked markTarget() has already put the board in the address bar,
    // and the hash and layer selection beside it are the rest of what the
    // sharer is looking at.
    const outcome = await shareView(location.href, name || document.title, {
      share: navigator.share ? (data) => navigator.share(data) : undefined,
      copy: navigator.clipboard ? (text) => navigator.clipboard.writeText(text) : undefined,
    });
    track('share', { outcome, from: 'board' });

    // Reports on itself, like the header button: it is the thing that was
    // pressed, so it is where the eye already is, and nothing is said when the
    // platform sheet handled it.
    const message = shareMessage(outcome);
    if (!message) return;
    button.title = message;
    button.classList.toggle('shared', outcome === 'copied');
    clearTimeout(restore);
    restore = setTimeout(() => {
      button.title = resting;
      button.classList.remove('shared');
    }, 2400);
  });
}

/**
 * Fixed points worth clicking. Rail stations come from Darwin's CRS list; the
 * harbour pin sits on the NorthLink terminal rather than the port centroid.
 */
interface Pin {
  lat: number; lon: number; label: string; refreshMs: number;
  render: (el: HTMLElement) => Promise<void>;
  /**
   * What /meta called it — and what `?go=pin:` names it by (#61).
   *
   * The kind rather than the label, because a label carries an emoji and the
   * place's own spelling of a harbour, and neither belongs in a link somebody
   * pastes into a message. It works because a place has at most one pin of
   * each non-rail kind, which is not an accident of pinsFor() but a rule
   * backend/test/pins.test.ts holds against the real packs.
   */
  kind: string;
  /**
   * The station code, on a rail pin only — what `?go=rail:` names (#61).
   *
   * Carried on the pin rather than left inside the render closure, because a
   * closure is not something a link can be resolved against: the code is in
   * there, and it is in there in a form nothing can read back.
   */
  crs?: string;
}

/**
 * Aberdeen's pins, kept only as the backend-down fallback.
 *
 * The live list comes from /meta — see pinsFromMeta() — because which pins
 * exist is a property of the place: Edinburgh's stations are not these, and
 * its Rosyth pin is a terminal, not a NorthLink board. This list survives for
 * the same reason the centre
 * fallback above it does: a backend that is down or slow should still yield
 * the map this deployment has always shown, not a blank one.
 */
const FALLBACK_PINS: Pin[] = [
  { lat: 57.2019, lon: -2.1978, label: '✈ ABZ', kind: 'airport', refreshMs: 10_000, render: renderAirport },
  // A minute, not 30 s: the board is a timetable now, and the interval only
  // keeps the countdown honest — the same cadence the bus station runs at.
  { lat: 57.1420, lon: -2.0790, label: '⚓ Ferries', kind: 'ferries', refreshMs: 60_000, render: renderFerries },
  // The bus station had no pin, so the one building most likely to be looked
  // for was reachable only by finding one of its stances among 1,481 stops.
  { lat: 57.1430, lon: -2.0956, label: '🚏 Bus station', kind: 'bus-station', refreshMs: 60_000, render: renderBusStation },
  { lat: 57.1436, lon: -2.0980, label: '🚆 Aberdeen', kind: 'rail', crs: 'ABD', refreshMs: 60_000, render: (el) => renderRail(el, 'ABD') },
  { lat: 57.2059, lon: -2.1906, label: '🚆 Dyce', kind: 'rail', crs: 'DYC', refreshMs: 60_000, render: (el) => renderRail(el, 'DYC') },
  // Reopened October 2020 and missing here until now.
  { lat: 57.2337, lon: -2.3466, label: '🚆 Kintore', kind: 'rail', crs: 'KTR', refreshMs: 60_000, render: (el) => renderRail(el, 'KTR') },
  { lat: 57.0596, lon: -2.1290, label: '🚆 Portlethen', kind: 'rail', crs: 'PLN', refreshMs: 60_000, render: (el) => renderRail(el, 'PLN') },
  { lat: 56.9678, lon: -2.2110, label: '🚆 Stonehaven', kind: 'rail', crs: 'STN', refreshMs: 60_000, render: (el) => renderRail(el, 'STN') },
  { lat: 57.2842, lon: -2.3776, label: '🚆 Inverurie', kind: 'rail', crs: 'INR', refreshMs: 60_000, render: (el) => renderRail(el, 'INR') },
];

/**
 * The place's pins, from what /meta declared.
 *
 * Kind maps to renderer here rather than the backend naming functions,
 * because what exists is the place's business and how to draw it is the
 * frontend's. An unrecognised kind is skipped rather than guessed at, so a
 * new backend pin kind degrades to absence — visible in review, harmless in
 * production — instead of a broken panel.
 */
function pinsFromMeta(meta: {
  pins?: { kind: string; label: string; lat: number; lon: number; crs?: string }[];
}): Pin[] {
  const pins: Pin[] = [];
  for (const p of meta.pins ?? []) {
    if (p.kind === 'airport') {
      pins.push({ ...p, refreshMs: 10_000, render: renderAirport });
    } else if (p.kind === 'ferries') {
      pins.push({ ...p, refreshMs: 60_000, render: renderFerries });
    } else if (p.kind === 'ferry-terminal') {
      // A terminal with no tracked route: nothing to refresh, so the interval
      // is long rather than zero — zero would mean "every frame" to setInterval.
      pins.push({ ...p, refreshMs: 3_600_000, render: renderFerryTerminal(p.label.replace(/^⚓\s*/, '')) });
    } else if (p.kind === 'bus-station') {
      pins.push({ ...p, refreshMs: 60_000, render: renderBusStation });
    } else if (p.kind === 'rail' && p.crs) {
      const crs = p.crs;
      pins.push({ ...p, crs, refreshMs: 60_000, render: (el) => renderRail(el, crs) });
    }
  }
  return pins.length > 0 ? pins : FALLBACK_PINS;
}

/**
 * What a deep link or a search result needs in order to reach a stop.
 *
 * Returned rather than exported as loose functions because they share the
 * stop cache and the toggle state. The board itself goes through the same
 * OpenDetail funnel as every pin — it briefly had its own timer and close()
 * beside wirePins', which is the two-owners state this comment always warned
 * about, and #75 is what it did in practice.
 */
export interface StopControls {
  /** The baked stop list, fetched once and shared with the toggles. */
  ensureLoaded(): Promise<readonly StopPoint[]>;
  /**
   * Switch this stop's layer on, so the map is actually drawing it.
   *
   * Not optional. The stops layers ship OFF, so flying to a stop without this
   * lands the reader on an empty street — the same failure revealFeature()
   * exists to prevent for the Now box's incident rows, in a worse form,
   * because there the layer was merely sometimes off and here it always is.
   */
  reveal(stop: StopPoint): void;
  /** Open its board, exactly as a click on it would. */
  open(stop: StopPoint): void;
  /**
   * Write the current toggle state onto whatever checkboxes exist right now.
   *
   * Because renderTransportBox() writes those rows as plain unchecked markup
   * and replaces the whole box when it does, and it runs off its own fetch —
   * so a deep link that reveals a stop before that fetch lands has its tick
   * thrown away by the render, leaving the stops drawn on the map with the row
   * for them unticked. The picker and the map silently disagreeing, which is
   * the failure the provider-row correction a few lines below it already
   * exists to prevent, arriving by the same door: state restored before the
   * markup that shows it.
   *
   * Nothing until now could reach that, because a click is by definition after
   * a render. #61 is the first caller that is not a click.
   */
  syncToggles(): void;
}

/**
 * Bus stops: fetched once, only when the layer is first switched on.
 *
 * 1,481 stops is about 100 kB of JSON, which is not free on a phone and is
 * pure waste for the majority who never turn the layer on. So the layer is off
 * by default and the payload is not requested until it is wanted — the same
 * reasoning that keeps MapLibre out of the critical path.
 */
function wireStops(map: MapAdapter, routes: RouteDrawer, openDetail: OpenDetail): StopControls {
  wireRiverCharts(map);

  // Through the one funnel, not a private timer. This closure used to hold its
  // own #detail handle, close() and interval beside the ones inside wirePins —
  // exactly the two-owners state both docstrings warn about — and the
  // documented failure was reachable: open the airport board, tap a bus stop,
  // and the airport's still-armed 10-second refresh rewrote the stop board,
  // after which the two boards alternated and #ap-close belonged to whichever
  // rendered last (#75). OpenDetail already carries everything a stop board
  // needs: the same-key toggle-close, the URL target (#61), a board that opens
  // expanded, and routes.wireRows on every render.
  const openStopBoard = (stop: StopPoint) => {
    // Key prefixed so a stop id can never collide with a pin label.
    openDetail(
      `stop:${stop.id}`,
      (el) => renderStopBoard(el, stop.id, stop.name),
      // A minute is plenty: the underlying data is a timetable and does not
      // change at all. This only keeps the countdown honest.
      60_000,
      { kind: 'stop', id: stop.id },
    );
  };

  map.onStopClick(openStopBoard);

  // Delegated rather than bound to the checkbox directly: the Transport box is
  // rendered after this runs and again on every status refresh, so a direct
  // listener would attach to nothing and then to a discarded element.
  // One fetch feeds every toggle: buses, trams and the Subway all arrive in
  // the same baked payload, and the split is a filter, not more requests.
  let cached: readonly StopPoint[] | null = null;
  const shown = { bus: false, tram: false, subway: false };

  /**
   * The baked stop list, fetched at most once.
   *
   * Memoised by promise rather than by a flag, for the reason the globe loader
   * gives for the same shape: a flag set before an await is a race, and two
   * callers arriving together — a toggle and a deep link, which is exactly
   * what a shared stop link produces — would each start their own download of
   * a payload that reaches 100 kB in Glasgow.
   *
   * A failed load clears the promise so the next caller retries, rather than
   * everybody awaiting a rejection for the rest of the session.
   */
  let loading: Promise<readonly StopPoint[]> | null = null;
  const ensureLoaded = (): Promise<readonly StopPoint[]> => {
    if (cached) return Promise.resolve(cached);
    loading ??= fetchStops()
      .then(({ s }) => {
        cached = s.map((stop) => ({
          ...stop, tram: stop.t === 1, subway: stop.s === 1, ferry: stop.f === 1,
        }));
        return cached;
      })
      .catch((err) => { loading = null; throw err; });
    return loading;
  };

  // Which kind a stop is, in the words the toggles use. Exactly one: the bake
  // makes the flags exclusive, so the first match is the truth. 'ferry' has
  // no toggle — a ferry-flagged stop is never handed to the map at all,
  // because the terminal is already there as the ⚓ Ferries pin and its
  // sailings render in that panel (the bake's flag feeds /ferries).
  const kindOf = (s: StopPoint) =>
    (s.tram ? 'tram' : s.subway ? 'subway' : s.ferry ? 'ferry' : 'bus') as
    keyof typeof shown | 'ferry';

  const COUNT_IDS = {
    bus: 'count-stops', tram: 'count-trams', subway: 'count-subways',
  } as const;

  const TOGGLE_SELECTOR = {
    bus: 'input[data-stops]', tram: 'input[data-trams]', subway: 'input[data-subways]',
  } as const;

  const syncToggles = () => {
    for (const kind of Object.keys(TOGGLE_SELECTOR) as (keyof typeof TOGGLE_SELECTOR)[]) {
      const box = document.querySelector<HTMLInputElement>(TOGGLE_SELECTOR[kind]);
      if (box) box.checked = shown[kind];
    }
  };

  const setCount = (id: string, text: string, warn = false) => {
    const count = document.getElementById(id);
    if (!count) return;
    count.textContent = text;
    count.classList.toggle('warn', warn);
  };

  const applyStopToggles = () => {
    if (!cached) return;
    map.setStops(cached.filter((s) => {
      const kind = kindOf(s);
      return kind !== 'ferry' && shown[kind];
    }));
    for (const kind of Object.keys(COUNT_IDS) as (keyof typeof COUNT_IDS)[]) {
      setCount(COUNT_IDS[kind], String(cached.filter((s) => kindOf(s) === kind).length));
    }
  };

  document.addEventListener('change', async (e) => {
    const checkbox = (e.target as HTMLElement | null)
      ?.closest<HTMLInputElement>('input[data-stops], input[data-trams], input[data-subways]');
    if (!checkbox) return;
    const key = checkbox.hasAttribute('data-trams') ? 'tram' as const
      : checkbox.hasAttribute('data-subways') ? 'subway' as const
        : 'bus' as const;
    shown[key] = checkbox.checked;

    if (!checkbox.checked) {
      // The open board may belong to a stop that just vanished; closing is
      // cheaper than checking, and it is what the single toggle always did.
      applyStopToggles();
      close();
      return;
    }

    if (!cached) {
      setCount(COUNT_IDS[key], '…');
      try {
        await ensureLoaded();
      } catch {
        setCount(COUNT_IDS[key], 'off', true);
        checkbox.checked = false;
        shown[key] = false;
        return;
      }
    }
    applyStopToggles();
  });

  /**
   * Switch on whichever toggle draws this stop.
   *
   * The checkbox is ticked as well as the flag set, because the picker and the
   * map disagreeing is its own bug — the one the layer-restore in main() is
   * careful about a few hundred lines down.
   */
  const reveal = (stop: StopPoint) => {
    const kind = kindOf(stop);
    // A ferry terminal is never drawn as a stop: the ⚓ pin is its mark, and
    // there is no toggle to switch on.
    if (kind === 'ferry') return;
    shown[kind] = true;
    // May tick nothing at all, if the Transport box has not rendered yet — see
    // syncToggles(), which is how it gets ticked in that case.
    syncToggles();
    applyStopToggles();
  };

  return { ensureLoaded, reveal, open: openStopBoard, syncToggles };
}

/**
 * Opens the detail panel on something that is not a pin.
 *
 * Returned by wirePins rather than exported, because the panel has exactly one
 * owner and two of them fighting over innerHTML — each with its own refresh
 * timer — is how a panel ends up flickering between two things.
 */
export type OpenDetail = (
  key: string,
  render: (el: HTMLElement) => Promise<void>,
  refreshMs: number,
  /**
   * What to name in the address bar while this is open, if anything (#61).
   *
   * Passed in rather than set by the caller after the fact, because `open` is
   * also the close: calling it with the key already showing shuts the panel,
   * and a caller marking the URL around that would announce a board that had
   * just been dismissed. One funnel, one writer.
   */
  target?: Target,
) => void;

/**
 * The one drawn route, and everything that can draw it.
 *
 * Owned here rather than inside a panel because the route is on the *map*, not
 * in a board: a line drawn from a bus stop is still there when the bus station
 * board replaces it, and two owners of that would be two ideas of which route
 * is currently drawn. It also happens to be the fix for a dead affordance —
 * `.dep-drawable` rows carry a pointer cursor and a hover highlight, and the
 * bus station board rendered them for its whole life with nothing listening,
 * because the click handler lived in wireStops and that board is drawn by
 * wirePins. Both call wireRows() now.
 */
interface RouteDrawer {
  /** Draw it, or clear it if it is the one already drawn. What a click does. */
  toggle(id: string): Promise<void>;
  /** Draw it and frame it. What a shared link does — see MapAdapter.frameRoute. */
  show(id: string): Promise<boolean>;
  /** Undraw whatever is drawn, and take it out of the URL. */
  clear(): void;
  /**
   * Attach the row handlers, and put the highlight back.
   *
   * Called after every render rather than once: a board re-renders itself once
   * a minute and replaces its own rows, so a handler bound at open time is
   * bound to an element that is no longer in the document — and the highlight
   * marking the drawn route goes with it.
   */
  wireRows(panel: HTMLElement): void;
}

function wireRoutes(map: MapAdapter): RouteDrawer {
  const panel = document.getElementById('detail')!;
  let drawn = '';

  /** Mark whichever visible row belongs to the drawn route, and unmark the rest. */
  const mark = () => {
    for (const row of panel.querySelectorAll<HTMLElement>('.dep-drawable')) {
      row.classList.toggle('dep-route-on', drawn !== '' && row.dataset.route === drawn);
    }
  };

  const clear = () => {
    drawn = '';
    map.setRoute([]);
    markRoute(null);
    mark();
  };

  const draw = async (id: string, frame: boolean): Promise<boolean> => {
    try {
      const route = await fetchRoute(id);
      drawn = id;
      map.setRoute(route.lines);
      markRoute(id);
      if (frame) map.frameRoute(route.lines);
      mark();
      return true;
    } catch {
      // A route with no geometry should not have been clickable; if the fetch
      // fails anyway, leaving the map alone beats drawing half a line. 118 of
      // Scotland's baked routes have departures and no shape — see
      // sources/bus-routes.ts, which counts them.
      return false;
    }
  };

  const toggle = async (id: string): Promise<void> => {
    if (!id) return;
    if (drawn === id) return clear();
    // Drawn where the map already is, not framed. Zooming out to fit the whole
    // route throws away the view the reader chose — usually a street they were
    // looking at — to show a shape they can reach themselves by pinching out.
    // Taking the camera is the expensive move; leave it. A shared link is the
    // one case where it is not, and that is show(), not this.
    await draw(id, false);
  };

  return {
    toggle,
    show: (id) => draw(id, true),
    clear,
    wireRows(el) {
      for (const row of el.querySelectorAll<HTMLElement>('.dep-drawable')) {
        row.classList.toggle('dep-route-on', drawn !== '' && row.dataset.route === drawn);
        row.addEventListener('click', () => void toggle(row.dataset.route ?? ''));
      }
    },
  };
}

/**
 * How a pin is named in a URL.
 *
 * Every pin gets one, because every pin is a fixed point — that is what the
 * interface above means by fixed, and it is the whole reason these and not the
 * incident lists can carry a link worth keeping.
 *
 * A station names itself by CRS, which is the code printed on its own
 * departure board and unique within a city. Everything else names itself by
 * kind, which is unique because a place has one airport and one harbour.
 */
function targetForPin(pin: Pin): Target {
  return pin.crs ? { kind: 'rail', id: pin.crs } : { kind: 'pin', id: pin.kind };
}

function wirePins(map: PinMap, pins: Pin[], routes: RouteDrawer): OpenDetail {
  const panel = document.getElementById('detail')!;
  let openLabel = '';
  let timer: number | undefined;

  function close(): void {
    openLabel = '';
    panel.classList.remove('visible');
    if (timer !== undefined) clearInterval(timer);
    timer = undefined;
    routes.clear();
    markTarget(null);
  }

  function wirePanelEvents(): void {
    panel.querySelector('#ap-close')?.addEventListener('click', close);
    wireBoardShare(panel);
    wireBoardCollapse(panel);
    // The bus station board renders .dep-drawable rows exactly as a stop board
    // does — pointer cursor, hover highlight — and until this line nothing
    // listened to them, because the handler lived in wireStops and this panel
    // is drawn by wirePins. They looked clickable and did nothing.
    routes.wireRows(panel);
    // Clicking a row centres the map on it — the point of a board on a map
    // rather than beside one.
    for (const li of panel.querySelectorAll<HTMLElement>('.mv[data-lat]')) {
      li.addEventListener('click', (event) => {
        // A row can contain an outbound ticket link. Without this, tapping it
        // opens the retailer *and* flies the map underneath, so the reader
        // comes back to a panel showing somewhere they did not choose.
        if ((event.target as HTMLElement)?.closest('a')) return;
        const lat = Number(li.dataset.lat);
        const lon = Number(li.dataset.lon);
        if (Number.isFinite(lat) && Number.isFinite(lon)) map.flyTo(lat, lon, 12);
      });
    }
    // Incident rows name the feature rather than a point, so they open its card
    // as well as centring — which is what "go to the relevant one" means.
    //
    // Revealed before focused, and that is not belt-and-braces. focusIncident
    // looks the id up in what the map is *currently drawing*, so a row for a
    // feature whose layer is switched off silently did nothing at all — see
    // revealFeature. That is how "2 new roadworks this week" opened a list of
    // two whose rows were dead: roadworks ship switched off, so none of their
    // features had ever reached the map.
    for (const li of panel.querySelectorAll<HTMLElement>('[data-incident]')) {
      li.addEventListener('click', () => {
        const id = li.dataset.incident;
        if (!id) return;
        revealFeature(id);
        map.focusIncident(id);
      });
    }
  }

  /** One way in, so pins and status lines cannot both own the panel. */
  const open: OpenDetail = (key, render, refreshMs, target) => {
    const refresh = async () => {
      try {
        await render(panel);
      } catch (err) {
        // Escaped: the message is ours or the browser's today, but an error
        // can quote whatever the server sent back.
        panel.innerHTML = `<p class="ap-empty">Could not load: ${
          escapeHtml(err instanceof Error ? err.message : String(err))}</p>`;
      }
      wirePanelEvents();
    };

    // Same source closes; a different one swaps content without flicker.
    if (openLabel === key) return close();
    if (timer !== undefined) clearInterval(timer);
    openLabel = key;
    // As with a stop board: a board that has just been opened shows itself.
    boardCollapsed = false;
    // Unconditional, including the null: opening a roadworks list over a
    // station board must take that station out of the URL, or the share button
    // hands out a link to whatever the panel used to be showing.
    markTarget(target ?? null);
    panel.classList.add('visible');
    panel.innerHTML = '<p class="ap-empty">Loading…</p>';
    void refresh();
    timer = window.setInterval(
      () => { if (!document.hidden) void refresh(); }, refreshMs);
  };

  for (const pin of pins) {
    map.addPin({
      lat: pin.lat,
      lon: pin.lon,
      label: pin.label,
      onClick: () => open(pin.label, pin.render, pin.refreshMs, targetForPin(pin)),
    });
  }

  return open;
}

/**
 * How close a link puts you to the stop it names.
 *
 * Above STOP_MIN_ZOOM in map.ts, and not by accident: below that the circles
 * are not drawn at all, so a link that landed at 13 would centre the map on a
 * stop the reader cannot see and then open a board about it.
 */
const TARGET_ZOOM = 16;

/**
 * A stop or station named in the URL (#61).
 *
 * Run from main() rather than from wireLive's first poll, and that is most of
 * the reason it is a separate function: stops come out of a baked payload that
 * owes nothing to the live feed, so resolving them there would make a shared
 * link to a bus stop die whenever an incident provider did — and wireLive
 * swallows that failure by design, so it would die silently. Feature links
 * stay in wireLive, because a feature that has not arrived cannot be focused.
 *
 * Never throws. A link with a stop id nobody recognises is the expected case
 * once a timetable has been rebuilt, not an error state.
 */
async function openLinkedTarget(
  map: MapAdapter,
  pins: readonly Pin[],
  stops: StopControls,
  openDetail: OpenDetail,
  routes: RouteDrawer,
): Promise<void> {
  return openTarget(map, pins, stops, openDetail, routes,
    parseTarget(location.search), parseRoute(location.search));
}

/**
 * Go to a target and open its board, wherever the target came from.
 *
 * Split out of openLinkedTarget so that the search box has no road of its own
 * (#56). Its issue asked for exactly this — "selecting a result should fly to
 * it and open the same popup a click would, no second code path" — and the
 * cost of a second one is not hypothetical: the reveal-before-fly rule below,
 * the exactly-one-pin rule, and the two honest messages about links that point
 * at nothing are four pieces of hard-won behaviour that a parallel
 * implementation would have to be told about, and would be told about only
 * after somebody noticed it was missing.
 */
async function openTarget(
  map: MapAdapter,
  pins: readonly Pin[],
  stops: StopControls,
  openDetail: OpenDetail,
  routes: RouteDrawer,
  target: Target | null,
  route: string | null,
): Promise<void> {
  if (!target || target.kind === 'feature') {
    // A route with no board is still a link worth honouring — somebody
    // trimmed the URL, or the stop it was shared from has since been
    // renumbered. Drawing it is the whole point of the link either way.
    if (route) void routes.show(route);
    return;
  }

  // Which of the two owns the camera. A named route is the bigger object and
  // the reason the link was sent, so it frames itself and the target's flyTo
  // would only be a jump on the way there — see MapAdapter.frameRoute for why
  // a link gets to take the camera at all when a click does not.
  const framing = route !== null;

  if (target.kind === 'rail' || target.kind === 'pin') {
    // Case-folded for rail because a CRS is shouted on every departure board in
    // the country and typed in whatever case the sender's phone felt like.
    // Pin kinds are lower-case slugs and get the same treatment for free.
    const wanted = target.id.toUpperCase();
    const matches = target.kind === 'rail'
      ? pins.filter((p) => p.crs?.toUpperCase() === wanted)
      : pins.filter((p) => p.kind.toUpperCase() === wanted);

    // Exactly one, not the first of several. Two pins answering to one name
    // would make the link resolve to whichever happened to sort first — the
    // collision backend/test/pins.test.ts forbids, handled here rather than
    // assumed away, because a test on the backend cannot bind a frontend that
    // may be talking to an older one.
    const pin = matches.length === 1 ? matches[0]! : undefined;
    if (!pin) {
      explainDeadLink(openDetail, `This link names something (${target.id}) that`
        + ' is not on this map. It may be on one of the other cities — see'
        + ' scot.live.');
      return;
    }
    if (!framing) map.flyTo(pin.lat, pin.lon, TARGET_ZOOM);
    openDetail(pin.label, pin.render, pin.refreshMs, targetForPin(pin));
    if (route) void routes.show(route);
    return;
  }

  let all: readonly StopPoint[];
  try {
    all = await stops.ensureLoaded();
  } catch {
    // The payload is 100 kB and the link is worth a second attempt on the next
    // toggle, which ensureLoaded() allows by clearing its own promise. Saying
    // nothing here beats saying "no such stop" about a stop that exists.
    return;
  }

  const stop = all.find((s) => s.id === target.id);
  if (!stop) {
    // The honest message, and the reason #61 chose the id it did: these are
    // the operator's own GTFS stop_ids, which survive a timetable rebuild
    // almost always and not quite always. The alternative was to bake the ATCO
    // code alongside every stop, which costs about 65 kB on a payload that is
    // already 100 kB — paid by everyone, on every visit, to keep a minority of
    // links alive a little longer. A link that says what happened is cheaper.
    explainDeadLink(openDetail, 'That stop is not in the current timetable.'
      + ' Stop numbers change when an operator rebuilds one, so a link kept for'
      + ' a while can point at a stop that has been renumbered.');
    return;
  }

  // Revealed before flown, for the reason the incident rows give at length:
  // the stops layers ship OFF, so a link that only moved the camera would land
  // the reader on an empty street and open a board floating over nothing.
  stops.reveal(stop);
  if (!framing) map.flyTo(stop.lat, stop.lon, TARGET_ZOOM);
  stops.open(stop);
  // After the board, not before: show() marks the row belonging to the drawn
  // route, and there are no rows to mark until the board has rendered. The
  // board's own wireRows() marks it again on every refresh after that.
  if (route) void routes.show(route);
}

/**
 * The search box (#56).
 *
 * The corpus is what this map already knows — the pins from /meta, the baked
 * stop list, and whatever the live layers are currently drawing. No geocoder,
 * so no usage policy, no attribution clause and no dependency on somebody
 * else's uptime for a search box to work.
 *
 * Assembled on every open rather than held: the stop list is 100 kB and most
 * readers never search, so a corpus built at boot charges everybody for a
 * feature some of them use — and the live half would be a snapshot of what the
 * map was drawing at boot, offering roadworks that finished this morning.
 *
 * Stops are folded by name before ranking. A city street has a stop on each
 * side and often several stances, and eight results that are all one street
 * read as a broken search while crowding out everywhere the reader might have
 * meant.
 */
function wireSearchBox(
  map: MapAdapter,
  pins: readonly Pin[],
  stops: StopControls,
  openDetail: OpenDetail,
  routes: RouteDrawer,
  city: string,
): void {
  const root = document.getElementById('search');
  const input = document.getElementById('search-input');
  const list = document.getElementById('search-results');
  const status = document.getElementById('search-status');
  const button = document.getElementById('search-link');
  if (!root || !list || !status || !button || !(input instanceof HTMLInputElement)) return;

  /**
   * The route list, fetched at most once a session.
   *
   * Memoised by promise rather than by a flag, for the reason ensureLoaded()
   * gives about the stops: a flag set before an await is a race, and two opens
   * in quick succession would each start their own download. A failure clears
   * it so the next open tries again rather than everybody awaiting a rejection
   * for the rest of the session.
   */
  let routeNames: Promise<readonly RouteName[]> | null = null;
  const ensureRoutes = (): Promise<readonly RouteName[]> => {
    routeNames ??= fetchRoutes()
      .then(({ r }) => r)
      .catch((err) => { routeNames = null; throw err; });
    return routeNames;
  };

  const corpus = async (): Promise<readonly SearchItem[]> => {
    const items: SearchItem[] = pins.map((pin) => ({
      id: pin.crs ? `rail:${pin.crs}` : `pin:${pin.kind}`,
      // The label carries the place's own emoji, which normalise() drops on
      // the way to matching and which reads well in a list.
      name: pin.label,
      kind: pin.crs ? 'station' : 'place',
      lat: pin.lat,
      lon: pin.lon,
      // A CRS is shouted on every departure board in the country, so somebody
      // will type it. As an alias, not a name: it must not outrank a place
      // actually called that.
      aka: pin.crs ? [pin.crs] : undefined,
      detail: pin.crs ? 'rail station' : undefined,
    }));

    items.push(...searchableFeatures());

    // Routes, by their number, which is what a reader types: "x8", "727", "3a".
    // Only routes that have a line, because that is what this endpoint serves
    // and because a hit that draws nothing is the dead row #39 spent a release
    // removing from the boards. No position: a route is a line across a city,
    // and the picker's list carries no geometry to take a centre from — see
    // SearchItem.lat.
    try {
      const list = await ensureRoutes();
      items.push(...list.map((r) => ({
        id: r.id,
        name: r.name,
        kind: 'route' as const,
        // The operator, because two of them can run a number: Aberdeen has a
        // 3 and a 3A from different companies, and "bus route" alone would
        // offer the reader two identical-looking rows.
        detail: r.operator ? `bus route · ${r.operator}` : 'bus route',
        aka: r.operator ? [r.operator] : undefined,
      })));
    } catch {
      // Same rule as the stops below: a list that does not arrive leaves
      // everything else searchable rather than emptying the box.
    }

    // Last, and allowed to fail on its own: a stop payload that does not
    // arrive should leave the stations and pins searchable rather than
    // emptying the box. ensureLoaded() clears its own promise on failure, so
    // the next open tries again.
    try {
      const all = await stops.ensureLoaded();
      items.push(...all.map((stop) => ({
        id: stop.id,
        name: stop.name,
        kind: stop.tram ? 'tram' : stop.subway ? 'subway' : 'stop',
        lat: stop.lat,
        lon: stop.lon,
      } as SearchItem)));
    } catch {
      // Reported by absence, not by a message: the box still works.
    }

    return dedupeStops(items, map.centre());
  };

  wireSearch({
    root,
    input,
    list,
    status,
    button,
    corpus,
    centre: () => map.centre(),
    rank,
    track,
    onPick: (item) => {
      track('search-go', { kind: item.kind, city });

      // A live feature is not a target openTarget resolves — those come off a
      // poll that can fail, which is why the deep link for them lives in
      // wireLive. Same two steps that a Now-box incident row takes, and in the
      // same order: revealed before focused, because focusIncident looks the
      // id up in what the map is currently drawing and the layer may be off.
      if (item.kind === 'feature') {
        revealFeature(item.id);
        map.focusIncident(item.id);
        // So a searched-to feature can be shared, which is the whole of what
        // #56 meant by pairing with #53.
        markTarget({ kind: 'feature', id: item.id });
        return;
      }

      // A route is not a place and has no board — it is a line on the map.
      // show() rather than toggle(): the reader asked for this one by name, so
      // drawing it and framing it is the answer, and toggling would undraw it
      // if it happened to be the one already drawn.
      if (item.kind === 'route') {
        void routes.show(item.id);
        return;
      }

      const target: Target = item.kind === 'station'
        ? { kind: 'rail', id: item.id.replace(/^rail:/, '') }
        : item.kind === 'place'
          ? { kind: 'pin', id: item.id.replace(/^pin:/, '') }
          : { kind: 'stop', id: item.id };
      // The link road, not a new one: reveal-before-fly, the exactly-one-pin
      // rule and the two honest not-on-this-map messages all live in there.
      void openTarget(map, pins, stops, openDetail, routes, target, null);
    },
  });
}

/**
 * Say why a link went nowhere, in the panel the board would have opened in.
 *
 * There is no toast in this UI and this is not the place to invent one: the
 * reader followed a link to a board, so the explanation belongs where the
 * board would have been rather than in a corner of the screen they have to
 * find. Built with textContent rather than innerHTML because the text quotes
 * the URL back, and the URL is the one thing here a stranger wrote.
 *
 * An hour, not zero: setInterval reads zero as "every frame", which is the
 * trap the ferry-terminal pin documents. Nothing in this panel changes anyway.
 */
function explainDeadLink(openDetail: OpenDetail, text: string): void {
  openDetail('dead-link', async (el) => {
    const head = document.createElement('div');
    head.className = 'ap-head';
    const title = document.createElement('h2');
    title.textContent = 'Not on this map';
    const dismiss = document.createElement('button');
    dismiss.id = 'ap-close';
    dismiss.setAttribute('aria-label', 'Close');
    dismiss.textContent = '\u00d7';
    head.append(title, dismiss);

    const body = document.createElement('p');
    body.className = 'ap-empty';
    body.textContent = text;

    el.replaceChildren(head, body);
  }, 3_600_000);
}

/**
 * Live Status card, grouped layer controls, and incident features.
 *
 * One /status call covers every provider, and one /live call every incident.
 * Two requests for the whole live picture rather than one per layer —
 * the difference is invisible on a desktop and very visible on a phone.
 */
async function wireLive(
  map: MapAdapter,
  openDetail: OpenDetail,
  /** Which city the backend says it is serving — see fetchGeometry(). */
  place: string,
  /** Only to put its ticks back after the Transport box is rebuilt below. */
  stops: StopControls,
): Promise<void> {
  const statusEl = document.getElementById('status-card')!;
  const transportEl = document.getElementById('box-transport')!;
  const environmentEl = document.getElementById('box-environment')!;
  const roadsEl = document.getElementById('box-roads')!;
  const eventsEl = document.getElementById('box-events')!;
  const hiddenProviders = new Set<string>();
  /**
   * Providers the layer picker knows about.
   *
   * The layer tree is fetched once, at load — it only changes when a provider
   * is added. But a provider *can* be added while a tab is open, and then the
   * live feed starts sending features the picker has never heard of. Before
   * this they drew unconditionally, because hiddenProviders is seeded from the
   * tree and a name absent from the tree is absent from that set too. The
   * result is a layer on the map with no row to switch it off, which is
   * exactly how journey times arrived unannounced on a tab that had been open
   * since before it shipped.
   *
   * Empty until the tree loads, and treated as "no opinion" while it is —
   * see draw(). Failing closed on an empty set would blank the whole map for
   * the moment before the first fetch returns, and permanently if it fails.
   */
  const knownProviders = new Set<string>();
  let features: LiveFeature[] = [];
  /**
   * Each feature's shape, where it has one — today the trunk-road path a
   * journey-time section is measured along.
   *
   * Fetched once, not on the poll: geometry changes when a road is rebuilt and
   * the reading on it changes every five minutes, so they travel separately.
   * Empty until it arrives and empty forever if it fails, and both are fine —
   * a section with no line keeps the stopwatch it always had.
   */
  let roadLines: Record<string, [number, number][]> = {};
  let zoneAreas: Record<string, { type: 'Polygon' | 'MultiPolygon'; coordinates: unknown }> = {};
  let deepLinked = false;
  /**
   * Which fuel grade the map labels and colours by. Remembered per browser,
   * through a guard, because storage throws outright in some privacy modes.
   */
  const FUEL_GRADE_KEY = 'fuel-grade';
  let fuelGrade: FuelGrade = (() => {
    try {
      return parseFuelGrade(window.localStorage.getItem(FUEL_GRADE_KEY));
    } catch {
      return 'petrol';
    }
  })();
  const setFuelGrade = (grade: FuelGrade) => {
    if (grade === fuelGrade) return;
    fuelGrade = grade;
    try {
      window.localStorage.setItem(FUEL_GRADE_KEY, grade);
    } catch {
      // A preference that does not survive a reload is no reason to fail.
    }
    for (const r of transportEl.querySelectorAll<HTMLInputElement>('input[data-fuel-grade]')) {
      r.checked = r.dataset.fuelGrade === grade;
    }
    draw();
    track(`fuel-grade:${grade}`, { grade });
  };

  /** A forecourt's status and band for the grade the Transport box chose. */
  const fuelSeverity = (f: LiveFeature): Pick<LiveFeature, 'status' | 'severity'> => {
    const v = fuelView(f, fuelGrade);
    return { status: v.label, severity: v.severity };
  };

  const draw = () => {
    map.setIncidents(features
      .filter((f) => !hiddenProviders.has(f.providerId))
      // A provider the picker cannot show a row for must not draw. Only once
      // the tree has actually loaded, so a failed or pending fetch leaves the
      // map as it was rather than emptying it.
      .filter((f) => knownProviders.size === 0 || knownProviders.has(f.providerId))
      .map((f) => (f.providerId === 'fuel' ? { ...f, ...fuelSeverity(f) } : f))
      .map((f) => ({
        id: f.id,
        providerId: f.providerId,
        lat: f.lat,
        lon: f.lon,
        // FUBAR markers carry their own brand red, so editorial reports are
        // distinguishable at a glance from utility incidents.
        colour: f.providerId === 'fubar' ? FUBAR_RED : SEVERITY_COLOUR[f.severity],
        severity: f.severity,
        // The whole list rides along for the camera cards, whose nearby note
        // says what the map already knows about the road the camera watches.
        html: renderIncident(f, features),
        // Fuel draws its price on the map. The provider already put the
        // headline grade in `status`, so there is nothing to re-derive here.
        // Sea stations draw their wave height the same way, for the same
        // reason: the number is why you would look.
        label: f.providerId === 'fuel' || f.providerId === 'waves' ? f.status : undefined,
        icon: f.icon,
        // The 3D card's three lines. Straight off the feature — the backend
        // already wrote a title and a one-line status for every layer, and a
        // second copy phrased for 3D would drift from the first.
        title: f.title,
        status: f.status,
        updatedAt: f.updatedAt,
        // Without this the radius expression reads 0 for everything and every
        // power cut draws the same size — the provider was sending the number
        // and this mapping was quietly dropping it.
        scale: f.scale,
        line: roadLines[f.id],
        // A filled boundary, when the provider published one. From /geometry
        // rather than from the feature, because it is large and never changes
        // — the same split the road lines above already make.
        zone: polygonOf(zoneAreas[f.id] ?? f.area),
        // The stopwatch colours, not the severity palette. A road painted in
        // one green and a marker drawn in another would read as two different
        // claims about the same section — see PALETTE and journeySprite().
        lineColour: LINE_COLOUR[f.icon ?? ''] ?? SEVERITY_COLOUR[f.severity],
        // The publisher's colour for a boundary they grade themselves. A Met
        // Office yellow warning is drawn yellow, amber orange, red red — the
        // name the reader already knows it by. Undefined for everything else,
        // which keeps a Low Emission Zone green (#70).
        zoneColour: zoneColour(f.level),
        // Colour the road, and mark it only where the colour is not "moving" —
        // the rule Traffic Scotland's own congestion map follows. See
        // isQuiet(), which is where the argument lives.
        //
        // A filled boundary is quiet for the same reason a coloured road is:
        // the shape already is the feature. A dot in the middle of a Low
        // Emission Zone marks nothing — there is no point at the centre of a
        // zone, and the marker read as a separate thing sitting inside it.
        quiet: isQuiet(f.icon ?? '', f.severity) || Boolean(zoneAreas[f.id]),
        // Cameras fade at the shared threshold — the sprite, the popup image
        // and the control-room tiles all read the same clock.
        stale: (f.imageAgeMs ?? 0) > CAMERA_STALE_MS,
      })));
  };

  /**
   * Switch a provider's layer on, and tick its row to say so.
   *
   * Extracted from the status-line handler, which is where this behaviour was
   * first needed and for a while the only place that had it. A line or a row
   * that offers to show you a feature has to be able to keep that offer whether
   * or not the layer happens to be on — and the reader must be able to see what
   * changed on their behalf, hence the checkbox.
   *
   * Returns early when there is nothing to do, so the common case of clicking
   * through to a layer already on costs no redraw.
   */
  const showProvider = (provider: string) => {
    if (!hiddenProviders.has(provider)) return;
    hiddenProviders.delete(provider);
    const box = document.querySelector<HTMLInputElement>(
      `input[data-provider="${provider}"]`);
    if (box) box.checked = true;
    draw();
  };

  // provider id → the label its own toggle carries. Empty until the layer tree
  // lands, which is fine: nothing is drawn before then either.
  const providerLabels = new Map<string, string>();
  /** A feature's layer, in the words the picker uses. */
  const providerLabel = (id: string) => providerLabels.get(id) ?? 'on the map now';

  // The panel's list rows reach layer state through here — see revealFeature.
  revealFeature = (featureId) => {
    const f = features.find((x) => x.id === featureId);
    if (f) showProvider(f.providerId);
  };

  /**
   * What the search box can offer off the live feed (#56).
   *
   * Read through a hook rather than handed over, because `features` is
   * replaced wholesale by every poll: a copy taken once would go on offering
   * a roadworks that finished this morning, and searching for it would fly
   * the reader to nothing.
   *
   * `providerId` travels with each item because a hit on a switched-off layer
   * is the failure revealFeature exists to prevent, and the search result is
   * the same shape of promise — the pick handler reveals before it focuses.
   * `title` is the feature's own words; the provider label under it is what
   * separates "Union Street" the camera from "Union Street" the stop, which
   * the issue asking for this named as the point.
   */
  searchableFeatures = () => features
    .filter((f) => Number.isFinite(f.lat) && Number.isFinite(f.lon) && f.title)
    .map((f) => ({
      id: f.id,
      name: f.title,
      kind: 'feature' as const,
      lat: f.lat,
      lon: f.lon,
      providerId: f.providerId,
      // The road number a camera's name was parsed from, so "A90" finds the
      // cameras on it. This is what #56 meant by searching roads: roadExtents
      // is a two-entry label map for grouping the camera list and carries no
      // coordinates, so there is no A90 to fly to — only the things on it.
      aka: f.road ? [f.road] : undefined,
      detail: providerLabel(f.providerId),
    }));

  /**
   * Deep link: /?go=feature:<id>, and /?marker=<id>, which is the same thing in
   * the older spelling. Centres and opens that marker — what FUBAR paste into a
   * Facebook post so readers land on the report itself rather than on the map
   * generally.
   *
   * Only features. Stops and stations are resolved in openLinkedTarget(), off
   * the live feed entirely, because they are baked data and would otherwise
   * inherit this function's dependence on a poll that can fail — silently, by
   * design, since refresh() swallows its own errors to keep the last good card
   * on screen.
   */
  const focusFromUrl = () => {
    const target = parseTarget(location.search);
    if (target?.kind !== 'feature') return;
    const wanted = featureIdCandidates(target.id);
    const match = features.find((f) => wanted.includes(f.id));
    // Revealed first, like every other way in: a link to a feature on a layer
    // that ships switched off would otherwise land on the map and do nothing,
    // which is the worst possible outcome for a link somebody has shared.
    if (match) { showProvider(match.providerId); map.focusIncident(match.id); }
  };

  // Layer controls are fetched once — the tree only changes when a provider is
  // added — and cached hard by the backend.
  try {
    const { groups } = await fetchLayers();

    // Seed the filter from the same `enabled` flag the checkboxes render from.
    // Without this the set starts empty, so every layer draws on load however
    // its box is ticked — the picker and the map silently disagreeing until
    // someone toggles one. A layer that is off by design should be off, not
    // off-looking.
    for (const group of groups) {
      for (const layer of group.layers) {
        knownProviders.add(layer.id);
        if (!layer.enabled || !layer.configured) hiddenProviders.add(layer.id);
        // What a searched feature calls itself underneath its own title, so
        // "Union Street" the camera and "Union Street" the stop are different
        // answers (#56). Taken from the layer tree rather than spelled again
        // here: it is the same word the reader ticked the box for.
        providerLabels.set(layer.id, layer.label);
      }
    }

    /*
     * The reader's view, before anything is drawn (#53).
     *
     * Applied here rather than after rendering because the checkboxes render
     * their own checked state from these two structures. Restoring the
     * selection afterwards would tick the map's layers on while leaving the
     * picker showing the defaults — the picker and the map silently
     * disagreeing, which is the exact failure the `enabled`/`configured`
     * seeding above already exists to avoid.
     */
    const layerDefaults = {
      kinds: { ...enabled },
      hiddenProviders: new Set(hiddenProviders),
      knownProviders: new Set(knownProviders),
    };
    // Read once, through a guard: storage throws outright in some privacy
    // modes rather than answering null, and a map that fails to start over a
    // remembered checkbox would be a poor trade.
    const store = (() => {
      try {
        return window.localStorage;
      } catch {
        return null;
      }
    })();

    const opening = applySelection(layerDefaults, openingSelection(location.search, store));
    for (const [kind, on] of Object.entries(opening.kinds)) enabled[kind as Kind] = on;
    hiddenProviders.clear();
    for (const id of opening.hiddenProviders) hiddenProviders.add(id);

    /** Put the view back in the address bar and in storage, after any toggle. */
    const rememberView = () => {
      const selection = selectionFor(layerDefaults, { kinds: enabled, hiddenProviders });
      if (store) writeStored(store, selection);
      // replaceState rather than pushState: toggling a layer is not a
      // navigation, and filling the history with twenty checkbox states would
      // make the back button useless for leaving the page. location.hash is
      // passed through unchanged so MapLibre's viewport survives the rewrite.
      history.replaceState(null, '',
        `${location.pathname}${selectionToSearch(selection, location.search)}${location.hash}`);
    };

    renderTransportBox(transportEl, groups, LAYERS, enabled, tramStopCount, subwayStopCount,
      fuelGrade);
    // That render replaced the stop rows with fresh unticked markup, so a deep
    // link that got here first has just had its tick thrown away — the stops
    // still on the map, the row for them off. Same correction as the provider
    // rows get below, for the same reason, at the only moment it can be made.
    stops.syncToggles();
    renderEnvironmentBox(environmentEl, groups);
    renderRoadsBox(roadsEl, groups);
    renderEventsBox(eventsEl, groups);
    // Now that the answer is in, the console can drop the button for a box this
    // place will never fill. renderEventsBox writes nothing rather than an
    // empty shell, so "did it render" and "is it empty" are the same question.
    if (!eventsEl.innerHTML) missingBoxes.add('events');
    // The render replaced each header, so the chevrons need their state back.
    panels.apply();

    // Provider rows render their checked state from the layer entry rather
    // than from `hiddenProviders`, so a restored selection has to be written
    // onto them after the fact. The mover kinds are already right —
    // renderTransportBox is handed `enabled` above.
    for (const box of [transportEl, environmentEl, roadsEl, eventsEl]) {
      for (const input of box.querySelectorAll<HTMLInputElement>('input[data-provider]')) {
        const id = input.dataset.provider;
        if (id) input.checked = !hiddenProviders.has(id);
      }
    }
    const onChange = (e: Event) => {
      const input = e.target as HTMLInputElement;
      if (input.dataset.fuelGrade) {
        // Not a layer toggle, so nothing for the view selection to remember.
        if (input.checked) setFuelGrade(parseFuelGrade(input.dataset.fuelGrade));
        return;
      }
      if (input.dataset.kind) {
        const kind = input.dataset.kind as Kind;
        enabled[kind] = input.checked;
        map.setLayerVisible(kind, input.checked);
        trackLayer(kind, input.checked);
      } else if (input.dataset.provider) {
        if (input.checked) hiddenProviders.delete(input.dataset.provider);
        else hiddenProviders.add(input.dataset.provider);
        draw();
        trackLayer(input.dataset.provider, input.checked);
      }
      rememberView();
    };
    transportEl.addEventListener('change', onChange);
    environmentEl.addEventListener('change', onChange);
    roadsEl.addEventListener('change', onChange);
    eventsEl.addEventListener('change', onChange);

    // The camera control room, opened from the Roads box. Delegated so it
    // survives any re-render, and dynamically imported so the overlay costs
    // nothing until somebody actually opens it — the MapLibre treatment.
    roadsEl.addEventListener('click', (e) => {
      if (!(e.target as HTMLElement | null)?.closest('#cr-open')) return;
      track('panel-show:control-room', { panel: 'control-room' });
      void import('./control-room.ts').then(({ openControlRoom }) => {
        openControlRoom({
          placeName,
          roadExtents,
          cameras: () => features.filter((f) => f.providerId === 'cameras'),
          // The same promise every list row makes: click this and you will
          // see the thing — so the layer is revealed before it is focused.
          focus: (id) => {
            revealFeature(id);
            map.focusIncident(id);
          },
        });
      });
    });
  } catch {
    transportEl.innerHTML = '';
    environmentEl.innerHTML = '';
    roadsEl.innerHTML = '';
    eventsEl.innerHTML = '';
  }

  /*
   * The shapes, once.
   *
   * Not awaited before the first draw and not retried: it is decoration on a
   * layer that works without it, so blocking the map on it would trade the
   * thing that matters for the thing that does not. When it lands it calls
   * draw() itself, and the roads colour in a moment after the markers appear.
   */
  void fetchGeometry(place)
    .then(({ lines, areas }) => { roadLines = lines; zoneAreas = areas ?? {}; draw(); })
    .catch(() => { /* No coloured roads this session. The stopwatches stand. */ });

  const refresh = async () => {
    try {
      const [status, live] = await Promise.all([fetchStatus(), fetchLiveFeatures()]);
      renderStatusCard(statusEl, status.items);
      // Lock the controls for anything not delivering. Done on every refresh
      // rather than once at load, so a feed that dies mid-session greys out and
      // one that recovers comes back without a reload.
      applyLayerHealth([transportEl, environmentEl, roadsEl, eventsEl], status.items);
      features = live.features;
      draw();
      if (!deepLinked) { deepLinked = true; focusFromUrl(); }

      // Two different numbers, deliberately. The layer row counts pins, which
      // is what ticking it draws; the button counts feeds, which is what the
      // control room shows — and a gantry with a camera each way is one pin
      // and two pictures. Both from the features actually served, so neither
      // can drift from what is on the map.
      const cameras = features.filter((f) => f.providerId === 'cameras');
      const feeds = cameras.reduce((n, f) => n + Math.max(1, f.views?.length ?? 1), 0);
      const cameraRow = document.getElementById('count-cameras');
      if (cameraRow) cameraRow.textContent = String(cameras.length);
      const crCount = document.getElementById('cr-count');
      if (crCount) crCount.textContent = `${feeds} feed${feeds === 1 ? '' : 's'}`;

      // Every status line is a way into the map: click it and the relevant
      // incident is centred and opened, per the spec.
      for (const li of statusEl.querySelectorAll<HTMLElement>('.st[data-clickable]')) {
        li.addEventListener('click', () => {
          const provider = li.dataset.provider!;

          // Rail has no map feature to centre: the trains are a vehicle layer
          // and the boards are a panel, so "3 trains delayed" was a line that
          // looked clickable and did nothing. It opens the disruption list
          // instead, whose rows fly to the station they are at.
          if (provider === 'rail') {
            openDetail('rail-disruption', renderRailDisruption, 60_000);
            return;
          }

          // Fuel expands into the ten cheapest forecourts for each grade,
          // rather than flying to the one cheapest. Picking a diesel row
          // switches the map to diesel, so the pin it opens shows the price
          // the reader just chose it by.
          if (provider === 'fuel') {
            openDetail('fuel-top', async (el) => {
              renderFuelTop(el, features);
              for (const row of el.querySelectorAll<HTMLElement>('[data-grade]')) {
                row.addEventListener('click', () =>
                  setFuelGrade(parseFuelGrade(row.dataset.grade)));
              }
            }, 60_000);
            return;
          }

          // A line that says which features it counted opens exactly those —
          // "4 new roadworks this week" lists the four, not the forty-six
          // long-running schemes sharing the layer. Split on '|' because a
          // DATEX record without an id falls back to "lat,lon", so a comma
          // cannot be the separator.
          if (li.dataset.list) {
            const wanted = new Set(li.dataset.list.split('|'));
            const label = li.querySelector('.st-text')?.textContent?.trim() || 'Details';
            openDetail(
              `list:${provider}:counted`,
              // Re-reads `features` each refresh, like the provider-wide list
              // below — a cleared incident leaves the list while it is open.
              async (el) => renderFeatureList(
                el, label, features.filter((f) => wanted.has(f.id)),
              ),
              60_000,
            );
            return;
          }

          // Several things behind one line means the line is a count, and a
          // count should open a list rather than pick one of them for you.
          // "2 power outages" opening whichever sorted first is the same dead
          // end the rail line had, wearing a different number.
          //
          // Fuel is the exception, and deliberately: its line names the
          // cheapest forecourt, so it has one subject even though the provider
          // has dozens of features.
          const mine = features.filter((f) => f.providerId === provider);
          if (mine.length > 1 && !li.dataset.focus) {
            const label = li.querySelector('.st-text')?.textContent?.trim() || 'Details';
            openDetail(
              `list:${provider}`,
              // Re-reads `features` on every refresh rather than closing over
              // the array, so a cleared outage leaves the list while it is open.
              async (el) => renderFeatureList(
                el, label, features.filter((f) => f.providerId === provider),
              ),
              60_000,
            );
            return;
          }

          // focusId names the feature the line is actually about — the cheapest
          // forecourt, not whichever one happens to sort first. Falling back to
          // the first only when the provider named none.
          const wanted = li.dataset.focus;
          const target = (wanted && features.find((f) => f.id === wanted))
            ?? features.find((f) => f.providerId === provider);
          if (target) {
            // The line is a promise that clicking shows the thing, so a layer
            // switched off still opens for it.
            showProvider(provider);
            map.focusIncident(target.id);
          }
        });
      }
    } catch {
      // Leave the previous card up rather than blanking it.
    }
  };

  void refresh();
  window.setInterval(() => { if (!document.hidden) void refresh(); }, 60_000);
}

/** Wind and sea state, refreshed slowly — the backend polls every 20 min. */
function wireWeather(): void {
  const el = document.getElementById('box-weather')!;
  const tick = async () => {
    try {
      const w = await fetchWeather();
      el.innerHTML = boxHead('Weather', 'weather');
      const body = document.createElement('div');
      renderWeather(body, w);
      el.append(body);
      panels.apply();
    } catch {
      el.innerHTML = '';
    }
  };
  void tick();
  window.setInterval(() => { if (!document.hidden) void tick(); }, 5 * 60_000);
}

/** When renderStatus last wrote the DOM — its 1 Hz gate. */
let statusWrittenAt = 0;

function renderStatus(): void {
  // Called from the frame loop so it appears and clears in step with the
  // vehicles, but everything written here changes at second granularity at
  // most — ages, counts, the outage notice. Writing identical strings into
  // the DOM sixty times a second is layout work a phone pays for, so run at
  // most once a second.
  const nowMs = Date.now();
  if (nowMs - statusWrittenAt < 1000) return;
  statusWrittenAt = nowMs;

  // The updating notice. Shown from the frame loop rather than on its own timer
  // so it appears and clears in step with everything else on screen.
  const outage = document.getElementById('outage');
  if (outage) {
    outage.hidden = apiDownSince === 0 || Date.now() - apiDownSince < OUTAGE_NOTICE_AFTER_MS;
  }

  for (const { kind } of LAYERS) {
    const el = document.getElementById(`count-${kind}`);
    if (!el) continue;
    el.textContent = String(counts[kind]);

    // Coaches deserve an explanation when empty. Ember run intercity on an
    // hourly service, so zero in the Aberdeen box is routine — but zero because
    // the feed is down looks identical without this.
    if (kind === 'buses') {
      el.title = busStats.ok
        ? `${busStats.inBox} in this area of ${busStats.fleet} Ember vehicles running`
          + (busStats.stale ? ` · ${busStats.stale} dropped as stale` : '')
        : 'coach feed unreachable';
      el.classList.toggle('warn', busStats.ok && busStats.fleet === 0);
      if (busStats.ok && counts.buses === 0 && busStats.fleet > 0) {
        el.textContent = `0/${busStats.fleet}`;
      }
    }

    // A dark train layer has three causes that look identical as a bare "0":
    // the geometry was never baked, no service is running, or nothing is
    // currently between two anchors. Say which — an unexplained zero sent us
    // looking in the wrong place once already.
    if (kind === 'trains') {
      if (!trainStats.ready) {
        el.textContent = 'off';
        el.classList.add('warn');
        el.title = trainStats.note || 'Track geometry not built — see docs/RAIL-POSITIONS.md';
      } else {
        el.classList.remove('warn');
        el.title = counts.trains > 0
          ? `${counts.trains} estimated from Darwin calling points along OSM track geometry`
          : 'No train is currently between two known calling points';
      }
    }
  }

  // The freshness reading lives on the Now card's header now, not in a pill
  // in the bottom-right corner. That element is rebuilt on every render of the
  // card, so this can find nothing — before the first render, or while the
  // card is showing its no-alerts variant, which asks statusHead for no time
  // slot. Silence is the right answer then rather than a thrown error: the
  // reading is a caption on the card, and a caption with no card is nothing.
  const status = document.getElementById('st-time');
  if (!status) return;

  // A failed basemap is the one problem a user cannot diagnose from the screen:
  // vehicles still move over an empty rectangle and it looks like our bug. Say so.
  if (basemapOk === false) {
    status.textContent = 'no basemap — set VITE_MAP_STYLE';
    status.classList.add('stale');
    return;
  }

  if (lastSuccessAt === 0) {
    status.textContent = 'connecting…';
    return;
  }
  const age = Math.round((Date.now() - lastSuccessAt) / 1000);
  // Say plainly when data is going stale. Showing minutes-old positions as if
  // they were live is worse than admitting the feed has stopped.
  status.textContent = age < 60 ? `live · updated ${age}s ago` : `stale · ${Math.round(age / 60)}m since last update`;
  status.classList.toggle('stale', age >= 60);
}

void main();
